/**
 * Integração de autenticação/autorização com o Clerk (https://clerk.com/docs).
 *
 * Estratégia: UMA aplicação Clerk para os quatro portais. A autorização por portal
 * é feita por *role* lida do `publicMetadata` do usuário (definido no dashboard do
 * Clerk ou via Backend API). Cada portal declara as roles que concedem acesso.
 *
 * O visual do login NÃO muda: usamos os fluxos *headless* do Clerk (`useSignIn`,
 * `useUser`, `useClerk`) por trás do `<LoginPage>` compartilhado, em vez dos
 * componentes prontos (`<SignIn/>`).
 *
 * A *publishable key* é pública por design e vai no bundle do browser. A *secret
 * key* NUNCA entra no frontend — ela pertence ao backend (.NET), que valida os
 * JWTs de sessão do Clerk.
 */
import type { CSSProperties, ReactNode } from 'react';
import { runtimeConfig } from './runtimeConfig';
import { emailLinkFailure } from './emailLink';
import {
  ClerkProvider,
  AuthenticateWithRedirectCallback,
  useSignIn,
  useSignUp,
  useUser,
  useClerk,
} from '@clerk/clerk-react';

/** Usuário do Clerk, derivado do retorno de `useUser` (evita depender de @clerk/types). */
export type TmrUser = NonNullable<ReturnType<typeof useUser>['user']>;

// ----------------------------------------------------------------------------
// Provider
// ----------------------------------------------------------------------------

/** Reads the publishable key from the runtime config, falling back to the build-time environment. */
export function clerkPublishableKey(): string {
  const key = runtimeConfig('VITE_CLERK_PUBLISHABLE_KEY');
  if (!key) {
    throw new Error(
      'VITE_CLERK_PUBLISHABLE_KEY ausente. Defina-a no `.env` da raiz do frontend ' +
        '(ex.: VITE_CLERK_PUBLISHABLE_KEY=pk_test_...) ou, em container, na env var do pod.',
    );
  }
  return key;
}

export interface TmrClerkProviderProps {
  children: ReactNode;
}

/**
 * Envolve a árvore no `<ClerkProvider>` com a configuração padrão dos portais TMR.
 * `routerPush`/`routerReplace` usam navegação real do browser para que o retorno
 * do SSO funcione em SPAs sem router (cada portal serve index.html em qualquer rota).
 */
export function TmrClerkProvider({ children }: TmrClerkProviderProps) {
  return (
    <ClerkProvider
      publishableKey={clerkPublishableKey()}
      afterSignOutUrl="/"
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
      routerPush={(to) => {
        window.location.href = to;
      }}
      routerReplace={(to) => {
        window.location.replace(to);
      }}
    >
      {children}
    </ClerkProvider>
  );
}

// ----------------------------------------------------------------------------
// Autorização por role
// ----------------------------------------------------------------------------

/**
 * Roles que concedem acesso a cada portal. A role vive no `publicMetadata` do
 * usuário no Clerk (`{ "roles": ["paciente"] }` ou `{ "role": "admin" }`).
 * Conceda múltiplas roles para liberar múltiplos portais ao mesmo usuário.
 */
export const TMR_PORTAL_ROLES: Record<string, string[]> = {
  paciente: ['paciente'],
  empresa: ['empresa'],
  cobranca: ['cobranca'],
  admin: ['admin'],
};

/** Extrai as roles do `publicMetadata` (aceita `roles: string[]` e/ou `role: string`). */
export function rolesFromUser(user: TmrUser | null | undefined): string[] {
  if (!user) return [];
  const meta = user.publicMetadata as { roles?: unknown; role?: unknown } | undefined;
  const roles = new Set<string>();
  if (meta) {
    if (Array.isArray(meta.roles)) {
      for (const r of meta.roles) if (typeof r === 'string') roles.add(r);
    }
    if (typeof meta.role === 'string') roles.add(meta.role);
  }
  return [...roles];
}

/** `true` quando o usuário tem ao menos uma das roles exigidas (vazio = liberado). */
export function hasAnyRole(userRoles: string[], required: string[]): boolean {
  if (required.length === 0) return true;
  return required.some((role) => userRoles.includes(role));
}

/**
 * Permissions (custom) exigidas por portal — modelo de autorização baseado em
 * Clerk Organizations (Roles → Permissions). O acesso ao portal é concedido a
 * QUALQUER role que tenha a(s) permission(s) abaixo (Admin, Support, etc.).
 *
 * As chaves seguem o formato do Clerk: `org:<feature>:<action>` e devem bater
 * EXATAMENTE com as keys criadas no dashboard (Organizations → Roles & Permissions).
 * Ex.: "Appointments.Reader" → `org:appointments:reader`.
 *
 * TODO: defina a permission específica de cada portal. Por ora todos exigem
 * `org:appointments:reader` para destravar o teste com uma única permission.
 */
export const TMR_PORTAL_PERMISSIONS: Record<string, string[]> = {
  paciente: ['org:appointments:reader'],
  empresa: ['org:appointments:reader'],
  cobranca: ['org:appointments:reader'],
  admin: ['org:appointments:reader'],
};

/** União das permissions de TODAS as organizations das quais o usuário é membro. */
export function permissionsFromUser(user: TmrUser | null | undefined): string[] {
  const set = new Set<string>();
  for (const m of user?.organizationMemberships ?? []) {
    for (const p of m.permissions ?? []) set.add(p);
  }
  return [...set];
}

/** Roles de organization do usuário (ex.: `org:admin`), de todas as memberships. */
export function orgRolesFromUser(user: TmrUser | null | undefined): string[] {
  const set = new Set<string>();
  for (const m of user?.organizationMemberships ?? []) {
    if (m.role) set.add(m.role);
  }
  return [...set];
}

/** `true` quando o usuário tem TODAS as permissions exigidas (vazio = liberado). */
export function hasPermission(userPermissions: string[], required: string[]): boolean {
  if (required.length === 0) return true;
  return required.every((perm) => userPermissions.includes(perm));
}

// ----------------------------------------------------------------------------
// Sessão
// ----------------------------------------------------------------------------

export type TmrAuthStatus = 'loading' | 'sso-callback' | 'signed-in' | 'signed-out';

export interface TmrAuth {
  status: TmrAuthStatus;
  user: TmrUser | null;
  /** Roles do usuário: roles de organization (ex.: `org:admin`) + `publicMetadata`. */
  roles: string[];
  /** Permissions (custom) reunidas de todas as organizations do usuário. */
  permissions: string[];
  signOut: () => Promise<void>;
}

const SSO_CALLBACK_PATH = '/sso-callback';

function isSsoCallbackRoute(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname.replace(/\/+$/, '');
  return path === SSO_CALLBACK_PATH;
}

/**
 * Estado de sessão do Clerk com as roles já resolvidas. Reporta também o estado
 * `sso-callback` (retorno do provedor OAuth), que o app deve renderizar com
 * `<SsoCallback/>`.
 */
export function useTmrAuth(): TmrAuth {
  const { isLoaded, isSignedIn, user } = useUser();
  const { signOut } = useClerk();

  let status: TmrAuthStatus;
  if (isSsoCallbackRoute()) status = 'sso-callback';
  else if (!isLoaded) status = 'loading';
  else if (isSignedIn) status = 'signed-in';
  else status = 'signed-out';

  const roles = [...new Set([...orgRolesFromUser(user), ...rolesFromUser(user)])];

  return {
    status,
    user: user ?? null,
    roles,
    permissions: permissionsFromUser(user),
    signOut: () => signOut(),
  };
}

// ----------------------------------------------------------------------------
// Handlers de login (ligados ao `<LoginPage>` compartilhado)
// ----------------------------------------------------------------------------

export type SsoProvider = 'google' | 'apple' | 'microsoft';

export interface ClerkLoginHandlers {
  /** Login e-mail/identificador + senha. */
  onSubmit: (creds: { identifier: string; password: string }) => Promise<void>;
  /** Login social via redirect (Google, Apple, Microsoft). */
  onSso: (provider: SsoProvider) => void;
  /**
   * Link mágico (email link). Requer a estratégia habilitada no Clerk.
   *
   * Resolve quando o e-mail sai; o que acontece depois — a pessoa clicar, ou clicar no lugar errado —
   * chega por `onOutcome`, porque só se sabe minutos mais tarde. Sem esse retorno a tela fica presa em
   * "Link enviado" para sempre, inclusive quando o Clerk já recusou a verificação.
   */
  onMagicLink: (identifier: string, onOutcome?: (error: Error) => void) => Promise<void>;
  /** Recuperação de senha — passo 1: dispara o e-mail com o código de reset. */
  onResetPassword: (email: string) => Promise<void>;
  /** Recuperação de senha — passo 2: verifica o código, define a nova senha e ativa a sessão. */
  onResetVerify: (params: { code: string; password: string }) => Promise<void>;
}

const OAUTH_STRATEGY: Record<SsoProvider, 'oauth_google' | 'oauth_apple' | 'oauth_microsoft'> = {
  google: 'oauth_google',
  apple: 'oauth_apple',
  microsoft: 'oauth_microsoft',
};

/** Erro de auth com `status` numérico, mapeado por `describeAuthError` do login-kit. */
class TmrAuthError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'TmrAuthError';
  }
}

/**
 * Código para um login que o Clerk não completou. Cada status vira um código próprio para a tela poder
 * dizer o que fazer; o genérico fica só para o que ainda não foi mapeado.
 */
function incompleteSignInCode(status: string | null): string {
  if (status === 'needs_new_password') return 'sign_in_needs_new_password';
  if (status === 'needs_second_factor') return 'sign_in_needs_second_factor';
  return `sign_in_incomplete:${status}`;
}

/** Normaliza um erro do Clerk para um `status` HTTP que gera a mensagem amigável certa. */
function rethrowClerkError(error: unknown): never {
  const e = error as { status?: number; errors?: Array<{ code?: string }> } | null;
  const code = e?.errors?.[0]?.code;

  // "A conta não entra por senha" NÃO é "a senha está errada", e tratar as duas como a mesma coisa manda a
  // pessoa conferir de novo algo que já está certo. É o que aconteceu com quem aceitou o convite pelo portal
  // hospedado do Clerk: a conta nasceu sem senha utilizável, a tela dizia "e-mail ou senha incorretos", e a
  // saída — "Esqueci minha senha", ali na mesma tela — nunca era sugerida.
  if (code === 'strategy_for_user_invalid') throw new TmrAuthError(401, 'sign_in_password_not_set');

  // Not a credential problem at all: the browser already holds a Clerk session, so nothing typed into
  // this form could have worked. It used to be lumped in with the wrong-password codes, which is how an
  // expired TMR session turned into "e-mail ou senha incorretos" — the password was never the issue.
  // The portal now avoids the form entirely in that state (`canRecoverSession`); this keeps any path
  // that still reaches it from blaming the operator.
  if (code === 'identifier_already_signed_in') throw new TmrAuthError(409, 'sign_in_already_signed_in');

  const credentialCodes = new Set([
    'form_password_incorrect',
    'form_identifier_not_found',
    'form_param_format_invalid',
    'form_param_nil',
  ]);

  if (code && credentialCodes.has(code)) throw new TmrAuthError(401, code);
  if (e?.status === 429) throw new TmrAuthError(429, 'rate_limited');
  if (typeof e?.status === 'number' && e.status >= 500) throw new TmrAuthError(500, 'server_error');
  if (error instanceof TypeError) throw error; // falha de rede do fetch
  if (typeof e?.status === 'number') throw new TmrAuthError(e.status, code ?? 'auth_error');
  throw error instanceof Error ? error : new Error(String(error));
}

const ssoRedirectUrl = () => `${window.location.origin}${SSO_CALLBACK_PATH}`;
const appHomeUrl = () => `${window.location.origin}/`;


/**
 * Handlers do `<LoginPage>` ligados ao Clerk (fluxos headless). Mantém o visual
 * intacto: o formulário padrão chama estes handlers; o Clerk cuida da autenticação.
 */
/** Variante "carregada" do retorno de `useSignIn` (com `signIn` e `setActive`). */
type LoadedSignIn = Extract<ReturnType<typeof useSignIn>, { isLoaded: true }>;

export function useClerkLoginHandlers(): ClerkLoginHandlers {
  const signInCtx = useSignIn();

  const requireLoaded = (): LoadedSignIn => {
    if (!signInCtx.isLoaded) throw new TmrAuthError(503, 'clerk_not_loaded');
    return signInCtx;
  };

  return {
    async onSubmit({ identifier, password }) {
      const { signIn, setActive } = requireLoaded();
      try {
        const result = await signIn.create({ identifier, password });
        if (result.status === 'complete') {
          await setActive({ session: result.createdSessionId });
          return;
        }
        // O Clerk aceitou as credenciais mas quer mais um passo. `needs_new_password` é o caso que
        // aparece de verdade: a conta existe sem senha utilizável, e a saída é o fluxo de redefinição
        // que esta mesma tela já oferece — dizer isso é melhor do que um erro genérico que não indica
        // nada. MFA continua sendo tela a construir.
        throw new TmrAuthError(401, incompleteSignInCode(result.status));
      } catch (error) {
        rethrowClerkError(error);
      }
    },

    onSso(provider) {
      if (!signInCtx.isLoaded) return;
      void signInCtx.signIn.authenticateWithRedirect({
        strategy: OAUTH_STRATEGY[provider],
        redirectUrl: ssoRedirectUrl(),
        redirectUrlComplete: appHomeUrl(),
      });
    },

    async onMagicLink(identifier, onOutcome) {
      const { signIn, setActive } = requireLoaded();
      try {
        const { startEmailLinkFlow } = signIn.createEmailLinkFlow();
        const created = await signIn.create({ identifier });
        const factor = created.supportedFirstFactors?.find((f) => f.strategy === 'email_link');
        if (!factor || !('emailAddressId' in factor)) {
          throw new TmrAuthError(401, 'magic_link_unavailable');
        }
        // Dispara o e-mail e começa o polling em segundo plano; a UI mostra "enviado"
        // assim que este handler resolve. Quando o link é clicado, a sessão é ativada.
        void startEmailLinkFlow({ emailAddressId: factor.emailAddressId, redirectUrl: appHomeUrl() })
          .then(async (res) => {
            if (res.status === 'complete') {
              await setActive({ session: res.createdSessionId });
              return;
            }
            // O polling terminou sem sessão. Antes isto era descartado junto com o `catch` vazio, e a tela
            // seguia dizendo "Link enviado" — inclusive quando o Clerk já tinha recusado a verificação.
            onOutcome?.(emailLinkFailure(res.firstFactorVerification?.status));
          })
          .catch(() => {
            onOutcome?.(new TmrAuthError(401, 'magic_link_expired'));
          });
      } catch (error) {
        rethrowClerkError(error);
      }
    },

    async onResetPassword(email) {
      const { signIn } = requireLoaded();
      try {
        await signIn.create({ strategy: 'reset_password_email_code', identifier: email });
      } catch (error) {
        rethrowClerkError(error);
      }
    },

    async onResetVerify({ code, password }) {
      const { signIn, setActive } = requireLoaded();
      try {
        // Em v5, código + nova senha vão juntos; conclui em uma chamada.
        const result = await signIn.attemptFirstFactor({
          strategy: 'reset_password_email_code',
          code,
          password,
        });
        if (result.status === 'complete') {
          await setActive({ session: result.createdSessionId });
          return;
        }
        // needs_second_factor (MFA) exigiria uma tela de 2FA (próximo passo).
        throw new TmrAuthError(401, `reset_incomplete:${result.status}`);
      } catch (error) {
        rethrowClerkError(error);
      }
    },
  };
}

/**
 * Conclui um convite do Clerk: aceita o ticket e define a senha na MESMA chamada (06 §6.1).
 *
 * Separar as duas coisas é exatamente o que quebrava antes — a conta nascia sem senha utilizável, o
 * primeiro login falhava e só a redefinição resolvia. O e-mail vem do próprio convite; não é nosso para
 * enviar, e não é do convidado para editar (é a chave de correlação, 05 §8).
 *
 * Vive aqui, e não na app, porque o Clerk é encapsulado por este pacote — nenhuma app importa
 * `@clerk/clerk-react` direto.
 */
export function useClerkInviteAcceptance() {
  const signUpCtx = useSignUp();

  return {
    isLoaded: signUpCtx.isLoaded,

    /** Cria a conta a partir do ticket e ativa a sessão. Lança um erro do Clerk para a tela traduzir. */
    async accept({ ticket, password }: { ticket: string; password: string }): Promise<void> {
      if (!signUpCtx.isLoaded) throw new TmrAuthError(503, 'clerk_not_loaded');
      const { signUp, setActive } = signUpCtx;

      let attempt = await signUp.create({ strategy: 'ticket', ticket, password });

      // `missing_requirements` é o Clerk dizendo que a instância pede algo que o ticket não trouxe. A
      // senha é a única que ainda temos em mãos — e ela precisa ir NESTA tentativa, a que segura o
      // ticket: o ticket é de uso único, então um segundo `create` ficaria sem ele e o convite morreria
      // aqui. É exatamente o modo de falha que esta tela existe para eliminar.
      if (attempt.status === 'missing_requirements' && attempt.missingFields.includes('password')) {
        attempt = await signUp.update({ password });
      }

      if (attempt.status === 'complete') {
        await setActive({ session: attempt.createdSessionId });
        return;
      }

      // O que sobrou é exigência da instância que esta tela não coleta (nome, aceite de termos) ou um
      // passo que ela não implementa (MFA). Vai com os campos nomeados, não só com o status: quem lê o
      // erro precisa saber o que desligar no Clerk, e `missing_requirements` sozinho não diz nada.
      const missing = attempt.missingFields.join(',');
      throw new TmrAuthError(422, `sign_up_incomplete:${missing || attempt.status}`);
    },
  };
}

// ----------------------------------------------------------------------------
// Views compartilhadas (retorno do SSO, acesso negado, sessão)
// ----------------------------------------------------------------------------

/** Processa o retorno do provedor OAuth e ativa a sessão. Renderize na rota /sso-callback. */
export function SsoCallback() {
  return <AuthenticateWithRedirectCallback signInFallbackRedirectUrl="/" signUpFallbackRedirectUrl="/" />;
}

const centeredWrap: CSSProperties = {
  minHeight: '100vh',
  display: 'grid',
  placeItems: 'center',
  padding: 24,
  background: 'var(--bg-canvas)',
};

function userLabel(user: TmrUser): string {
  return (
    user.fullName ??
    user.primaryEmailAddress?.emailAddress ??
    user.username ??
    user.emailAddresses?.[0]?.emailAddress ??
    user.id
  );
}

export interface ClerkSessionPanelProps {
  /** Nome do portal, ex.: "Portal do Paciente". */
  title: string;
  user: TmrUser;
  roles: string[];
  permissions?: string[];
  onSignOut: () => void | Promise<void>;
}

/**
 * View autenticada mínima (substitui a antiga `SessionPanel` baseada em BFF).
 * Confirma a sessão do Clerk e mostra identidade + roles. As chamadas a APIs
 * protegidas usarão o JWT do Clerk validado no backend (próximo passo).
 */
export function ClerkSessionPanel({ title, user, roles, permissions, onSignOut }: ClerkSessionPanelProps) {
  return (
    <div style={centeredWrap}>
      <div className="tmr-card" style={{ maxWidth: 460, width: '100%' }}>
        <p className="tmr-eyebrow">{title}</p>
        <h1 className="tmr-h3" style={{ marginTop: 6 }}>
          Você está conectado
        </h1>
        <p className="tmr-body-sm" style={{ color: 'var(--fg-muted)', marginTop: 4 }}>
          {userLabel(user)}
        </p>
        {roles.length > 0 && (
          <p className="tmr-body-sm" style={{ marginTop: 8, color: 'var(--fg-subtle)' }}>
            Papéis: {roles.join(', ')}
          </p>
        )}
        {permissions && permissions.length > 0 && (
          <p className="tmr-body-sm" style={{ marginTop: 4, color: 'var(--fg-subtle)' }}>
            Permissions: {permissions.join(', ')}
          </p>
        )}
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button className="tmr-btn tmr-btn--secondary" onClick={() => void onSignOut()}>
            Sair
          </button>
        </div>
      </div>
    </div>
  );
}
